<template>
  <div>
    <b-notification :closable="false" :has-icon="true" icon="database">
      <span v-html="$t('compCarbonFormsSurveySurveyConfig.info-1.main')" />
      <ul style="list-style-type: square; margin-left: 25px">
        <li>
          <span v-html="$t('compCarbonFormsSurveySurveyConfig.info-1.item1')" />
        </li>
        <li>
          <span v-html="$t('compCarbonFormsSurveySurveyConfig.info-1.item2')" />
        </li>
        <li v-if="withUpload">
          <span
            v-html="$t('compCarbonFormsSurveySurveyConfig.info-1.item3.a')"
          />
          <a class="link" @click="displayFormat = true">
            {{ $t('compCarbonFormsSurveySurveyConfig.info-1.item3.b') }}</a
          >
          <span
            v-html="$t('compCarbonFormsSurveySurveyConfig.info-1.item3.c')"
          />
          <a class="link" href="/static/carbon/Questionnaire_ddt.rtf" download>
            {{ $t('compCarbonFormsSurveySurveyConfig.info-1.item3.d') }}</a
          >
          <span
            v-html="$t('compCarbonFormsSurveySurveyConfig.info-1.item3.e')"
          />
        </li>
      </ul>

      <br />
      <div class="columns">
        <div class="column is-offset-1 is-10">
          <b-field :type="clonableType">
            <p class="control">
              <b-dropdown
                :disabled="isSubmitted || surveyActive || isClonedSurvey"
                v-model="surveyAction"
                aria-role="list"
              >
                <template
                  v-if="surveyAction === 'activate'"
                  #trigger="{ active }"
                >
                  <b-button
                    :label="$t('compCarbonFormsSurveySurveyConfig.field-1')"
                    :type="clonableType"
                    icon-left="link"
                    :icon-right="active ? 'caret-up' : 'caret-down'"
                    data-testId="dropdown-menu"
                  />
                </template>
                <template
                  v-else-if="surveyAction === 'clone'"
                  #trigger="{ active }"
                >
                  <b-button
                    :label="$t('compCarbonFormsSurveySurveyConfig.field-3')"
                    :type="clonableType"
                    icon-left="clone"
                    :icon-right="active ? 'caret-up' : 'caret-down'"
                  />
                </template>
                <template v-else #trigger="{ active }">
                  <b-button
                    :label="$t('upload')"
                    :type="clonableType"
                    icon-left="upload"
                    :icon-right="active ? 'caret-up' : 'caret-down'"
                  />
                </template>
                <b-dropdown-item value="activate">
                  <div class="media">
                    <b-icon class="media-left" icon="link"></b-icon>
                    <div class="media-content">
                      <h3>
                        {{ $t('compCarbonFormsSurveySurveyConfig.field-1') }}
                      </h3>
                    </div>
                  </div>
                </b-dropdown-item>
                <b-dropdown-item value="clone" v-if="!surveyActive">
                  <div class="media">
                    <b-icon class="media-left" icon="clone"></b-icon>
                    <div class="media-content">
                      <h3>
                        {{ $t('compCarbonFormsSurveySurveyConfig.field-3') }}
                      </h3>
                    </div>
                  </div>
                </b-dropdown-item>
                <b-dropdown-item value="upload" v-if="withUpload">
                  <div class="media">
                    <b-icon class="media-left" icon="upload"></b-icon>
                    <div class="media-content">
                      <h3>{{ $t('upload') }}</h3>
                    </div>
                  </div>
                </b-dropdown-item>
              </b-dropdown>
            </p>
            <b-input
              v-if="surveyAction === 'activate'"
              expanded
              v-model="link"
              :type="clonableType"
              :readonly="true"
              data-testId="survey-link"
            ></b-input>
            <b-input
              v-else-if="surveyAction === 'clone'"
              :type="clonableType"
              expanded
              :readonly="true"
              :value="cloneText"
            ></b-input>
            <b-select
              v-else
              expanded
              v-model="buffer"
              @input="$emit('input', buffer)"
            >
              <option :value="null">
                {{ $t('compCarbonFormsSurveySurveyConfig.overview') }}
              </option>
              <option
                v-for="(source, key) in sources"
                :key="key"
                :value="source"
              >
                {{ $t('compCarbonFormsSurveySurveyConfig.label-1') }}
                {{ source }}
              </option>
            </b-select>
            <p v-if="surveyAction === 'activate'" class="control">
              <b-tooltip type="is-black" :label="activeCommutesLabel">
                <b-button
                  :disabled="isSubmitted"
                  icon-left="bicycle"
                  class="button"
                  :type="activateType('commutes')"
                  @click="activate('commutes')"
                  data-testId="commutes"
                />
              </b-tooltip>
            </p>
            <p v-if="surveyAction === 'activate'" class="control">
              <b-tooltip type="is-black" :label="activeFoodsLabel">
                <b-button
                  :disabled="isSubmitted"
                  icon-left="cutlery"
                  class="button"
                  :type="activateType('foods')"
                  @click="activate('foods')"
                  data-testId="foods"
                />
              </b-tooltip>
            </p>
            <p class="control">
              <b-tooltip
                v-if="surveyAction === 'activate'"
                type="is-black"
                :label="$t('compCarbonFormsSurveySurveyConfig.config')"
              >
                <b-button
                  :disabled="isSubmitted"
                  icon-left="cogs"
                  class="button"
                  @click="displayModal = true"
                  data-testId="survey-settings"
                />
              </b-tooltip>
              <b-button
                v-else-if="surveyAction === 'clone'"
                :disabled="isSubmitted || !isClonable"
                class="button"
                :type="clonableType"
                @click="clone"
                data-testId="clone-survey"
              >
                <span v-if="!isClonedSurvey">{{
                  $t('compCarbonFormsSurveySurveyConfig.field-3')
                }}</span>
                <span v-else>{{
                  $t('compCarbonFormsSurveySurveyConfig.field-4')
                }}</span>
              </b-button>
            </p>
            <p class="control" v-if="surveyAction === 'upload'">
              <b-upload v-model="dropFile" data-testid="commutes-upload">
                <b-tooltip
                  :label="$t('compCarbonFormsSurveySurveyConfig.label-3')"
                  type="is-black"
                  position="is-top"
                >
                  <a class="button">
                    <b-icon icon="upload"></b-icon>
                  </a>
                </b-tooltip>
              </b-upload>
            </p>
            <p class="control" v-if="surveyAction === 'upload'">
              <b-tooltip
                :label="$t('compCarbonFormsSurveySurveyConfig.tooltip-1')"
                type="is-black"
                position="is-top"
              >
                <b-button
                  :disabled="submitted || nitems === 0"
                  @click="del"
                  icon-left="trash"
                />
              </b-tooltip>
            </p>
          </b-field>
        </div>
      </div>
    </b-notification>

    <!-- Information bloc -->
    <b-notification :closable="false" :has-icon="true" icon="info">
      <p v-if="surveyAction === 'activate'">
        <span v-html="$t('compCarbonFormsSurveySurveyConfig.info-2')" />
        <br />
        <br />
        <span v-html="$t('compCarbonFormsSurveySurveyConfig.info-3')" />
      </p>
      <span
        v-else-if="surveyAction === 'clone'"
        v-html="$t('compCarbonFormsSurveySurveyConfig.info-4')"
      />
      <span
        v-else-if="surveyAction === 'upload'"
        v-html="$t('compCarbonFormsSurveySurveyConfig.info-6')"
      />

      <b-modal
        :active.sync="displayModal"
        has-modal-card
        :can-cancel="['escape', 'x', 'outside']"
        :width="1000"
      >
        <div class="modal-card" style="width: 1000px">
          <header class="modal-card-head">
            <p class="modal-card-title">
              {{ $t('compCarbonFormsSurveySurveyConfig.title-1') }}
            </p>
          </header>
          <section class="modal-card-body">
            <b-notification :closable="false">
              <span v-html="$t('compCarbonFormsSurveySurveyConfig.info-5')" />
            </b-notification>
            <b-notification :closable="false">
              <b-field :label="$t('compCarbonFormsSurveySurveyConfig.field-5')">
                <b-input
                  type="textarea"
                  :disabled="isSubmitted"
                  maxlength="500"
                  expanded
                  v-model="surveyMessage"
                ></b-input>
              </b-field>
            </b-notification>
            <b-notification :closable="false">
              <tags-input :only1PerCategory="false" v-model="surveyTags" />
            </b-notification>
            <nav class="level">
              <div class="level-left"></div>
              <div class="level-right">
                <b-button
                  class="level-item"
                  @click="displayModal = false"
                  icon-left="close"
                >
                  {{ $t('cancel') }}
                </b-button>
                <b-button
                  class="level-item"
                  icon-left="save"
                  :disabled="isSubmitted"
                  type="is-primary"
                  @click="saveSurveyConfig"
                >
                  <span>{{ $t('save') }}</span>
                </b-button>
              </div>
            </nav>
          </section>
        </div>
      </b-modal>
    </b-notification>

    <!-- Error managment -->
    <upload-errors
      v-if="withUpload"
      :active="displayErrors"
      :error="error"
      :module="module"
      @close="displayErrors = false"
    >
    </upload-errors>

    <!-- File format -->
    <b-modal v-if="withUpload" :active.sync="displayFormat" has-modal-card>
      <div class="modal-card" style="width: 900px; height: auto">
        <header class="modal-card-head">
          <p class="modal-card-title">
            {{
              $t('compCarbonFormsSurveySurveyConfig.title-modal-file-format')
            }}
          </p>
        </header>
        <section class="modal-card-body">
          <help-file-format />
        </section>
      </div>
    </b-modal>
  </div>
</template>

<script>
import Modules from '@/models/Modules.js'
import { useGHGIStore } from '@/stores/ghgi.js'
import { useAdminStore } from '@/stores/admin.js'
import { useCoreStore } from '@/stores/core.js'
import { parseFile } from '@/utils/parser.js'

const MAX_FILE_SIZE = 1024 * 512 // 500 ko

export default {
  name: 'SurveyConfig',
  props: {
    module: {
      type: String,
      default: null
    },
    value: {
      type: String,
      default: ''
    },
    formatDescription: {
      type: [Object, Function],
      default: null
    },
    callback: {
      type: Function,
      default: null
    },
    submitted: {
      type: Boolean,
      default: false
    },
    withUpload: {
      type: Boolean,
      default: false
    }
  },
  setup () {
    const adminStore = useAdminStore()
    const ghgiStore = useGHGIStore()
    const coreStore = useCoreStore()
    return { adminStore, ghgiStore, coreStore }
  },
  data () {
    return {
      surveyAction: 'activate',
      currentHost: null,
      displayModal: false,
      displayFormat: false,
      dropFile: null,
      displayErrors: false,
      error: null,
      buffer: this.value
    }
  },
  components: {
    'tags-input': () => import('@/components/core/TagsInput.vue'),
    'upload-errors': () => import('@/components/carbon/forms/UploadErrors.vue'),
    'help-file-format': () =>
      import('@/components/carbon/forms/commutes/HelpFileFormat.vue')
  },
  computed: {
    year: function () {
      return this.ghgiStore.item.year
    },
    commutes: function () {
      return this.ghgiStore.items(Modules.COMMUTES)
    },
    surveyActive: function () {
      return this.ghgiStore.item.surveyActive
    },
    isSubmitted: function () {
      return this.ghgiStore.item.isSubmitted
    },
    isClonedSurvey: function () {
      return this.ghgiStore.isClonedSurvey
    },
    link: function () {
      return (
        'http://' + this.currentHost + '/survey/' + this.ghgiStore.item.uuid
      )
    },
    surveyMessage: {
      get: function () {
        return this.ghgiStore.item.surveyMessage
      },
      set: function (message) {
        this.ghgiStore.updateSurveyMessage(message)
      }
    },
    surveyTags: {
      get: function () {
        return this.ghgiStore.item.surveyTags
      },
      set: function (tags) {
        this.ghgiStore.updateSurveyTags(tags)
      }
    },
    activeCommutesLabel: function () {
      if (this.ghgiStore.item.commutesActive) {
        return this.$t('compCarbonFormsSurveySurveyConfig.desactivate-commutes')
      } else {
        return this.$t('compCarbonFormsSurveySurveyConfig.activate-commutes')
      }
    },
    activeFoodsLabel: function () {
      if (this.ghgiStore.item.foodsActive) {
        return this.$t('compCarbonFormsSurveySurveyConfig.desactivate-foods')
      } else {
        return this.$t('compCarbonFormsSurveySurveyConfig.activate-foods')
      }
    },
    actionIcon: function () {
      if (this.surveyAction === 'clone') {
        return 'clone'
      } else {
        return 'link'
      }
    },
    cloneText: function () {
      if (this.year === 2021) {
        return this.$t('compCarbonFormsSurveySurveyConfig.error-msg-1')
      } else if (this.year === 2020) {
        return this.$t('compCarbonFormsSurveySurveyConfig.error-msg-2')
      } else {
        if (this.previousGHGIYear) {
          if (this.previousGHGIYear.surveyCloneYear) {
            return (
              this.$t('compCarbonFormsSurveySurveyConfig.error-msg-4') +
              ' ' +
              this.previousGHGIYear.year +
              ' ' +
              this.$t('compCarbonFormsSurveySurveyConfig.error-msg-5')
            )
          } else {
            if (this.commutes.length > 0 && !this.previousGHGIYear) {
              return this.$t('compCarbonFormsSurveySurveyConfig.error-msg-3')
            } else {
              return (
                this.$t('compCarbonFormsSurveySurveyConfig.error-msg-4') +
                ' ' +
                this.previousGHGIYear.year
              )
            }
          }
        } else {
          return (
            this.$t('compCarbonFormsSurveySurveyConfig.error-msg-6') +
            ' ' +
            (this.year - 1)
          )
        }
      }
    },
    previousGHGIYear: function () {
      let allGHGIYears = this.adminStore.allGHGI.filter(
        (obj) => obj.year === this.year - 1
      )
      if (allGHGIYears.length > 0) {
        return allGHGIYears[0]
      } else {
        return null
      }
    },
    isClonable: function () {
      if (this.year === 2021) {
        return false
      } else if (this.year === 2020) {
        return false
      } else {
        if (this.previousGHGIYear) {
          if (this.previousGHGIYear.surveyCloneYear) {
            return false
          } else {
            if (this.commutes.length > 0 && !this.previousGHGIYear) {
              return false
            } else {
              return true
            }
          }
        } else {
          return false
        }
      }
    },
    clonableType: function () {
      if (this.surveyAction === 'clone') {
        if (this.isClonable) {
          return 'is-primary'
        } else {
          return 'is-danger'
        }
      } else {
        return 'is-primary'
      }
    },
    nitems: function () {
      return this.ghgiStore.items(this.module).length
    },
    sources: function () {
      return Array.from(
        new Set(
          this.ghgiStore
            .items(this.module)
            .filter((obj) => obj.source !== null)
            .map((obj) => obj.source)
        )
      )
    }
  },
  methods: {
    activate: function (module = null) {
      const loadingComponent = this.$buefy.loading.open({
        container: null
      })
      this.ghgiStore
        .updateSurveyActive(module)
        .then((data) => {
          loadingComponent.close()
        })
        .catch((erreur) => {
          loadingComponent.close()
        })
    },
    activateType: function (module) {
      if (module === Modules.COMMUTES) {
        if (this.ghgiStore.item.commutesActive) {
          return 'is-primary'
        } else {
          return ''
        }
      } else if (module === Modules.FOODS) {
        if (this.ghgiStore.item.foodsActive) {
          return 'is-primary'
        } else {
          return ''
        }
      }
    },
    saveSurveyConfig: function () {
      const loadingComponent = this.$buefy.loading.open({
        container: null
      })
      this.ghgiStore
        .saveSurveyConfig({
          surveyMessage: this.surveyMessage,
          surveyTags: this.surveyTags
        })
        .then((data) => {
          loadingComponent.close()
          this.$buefy.toast.open({
            type: 'is-light',
            message: this.$t('compCarbonFormsSurveySurveyConfig.save-msg-1')
          })
          this.displayModal = false
        })
        .catch((erreur) => {
          loadingComponent.close()
        })
    },
    clone: function () {
      const loadingComponent = this.$buefy.loading.open({
        container: null
      })
      let surveyCloneYear = this.year - 1
      if (this.isClonedSurvey) {
        surveyCloneYear = null
      }
      this.ghgiStore
        .cloneSurvey(surveyCloneYear)
        .then((data) => {
          this.$emit('is-valid', surveyCloneYear)
          loadingComponent.close()
        })
        .catch((erreur) => {
          loadingComponent.close()
        })
    },
    openLink: function () {
      window.open(this.link, '_blank')
    },
    del: function () {
      let self = this
      let msg =
        this.$t('compCarbonFormsSurveySurveyConfig.delete-msg-file') +
        ' <strong>' +
        this.value +
        '</strong> ?'
      let source = this.value
      if (this.value === null) {
        msg = this.$t('compCarbonFormsSurveySurveyConfig.delete-msg-all')
        source = null
      }
      this.$buefy.dialog.confirm({
        title: self.$t('compCarbonFormsSurveySurveyConfig.delete-msg-title'),
        message: msg,
        confirmText: self.$t('validate'),
        cancelText: self.$t('cancel'),
        canCancel: ['escape', 'button', 'outside'],
        onConfirm: function () {
          self.ghgiStore.deleteSource({
            module: self.module,
            source: source
          })
          self.$emit('update')
          self.buffer = null
          self.$emit('input', self.buffer)
          self.dropFile = null
        }
      })
    },
    processFile () {
      if (!this.sources.includes(this.dropFile.name)) {
        this.$emit('update')
        parseFile(this.dropFile, this.formatDescription, this.callback)
          .then((results) => {
            // display all data
            this.buffer = null
            this.$emit('input', this.buffer)
          })
          .catch((error) => {
            this.error = error
            this.displayErrors = true
          })
      } else {
        this.$buefy.dialog.alert({
          title: this.$t('compCarbonFormsSurveySurveyConfig.error-msg-title'),
          message: this.$t(
            'compCarbonFormsSurveySurveyConfig.error-msg-duplicate'
          )
        })
      }
    }
  },
  watch: {
    isClonedSurvey: function () {
      this.surveyAction = 'clone'
    },
    dropFile: function () {
      this.error = null
      if (this.dropFile) {
        // if file size > MAX_FILE_SIZE warn user for processing time
        let self = this
        if (this.dropFile.size > MAX_FILE_SIZE) {
          this.$buefy.dialog.confirm({
            title: self.$t('compCarbonFormsSurveySurveyConfig.file-size.title'),
            message: self.$t('compCarbonFormsSurveySurveyConfig.file-size.msg'),
            confirmText: self.$t('validate'),
            cancelText: self.$t('cancel'),
            onConfirm: () => self.processFile()
          })
        } else {
          this.processFile()
        }
      }
    },
    value: function () {
      this.buffer = this.value
    }
  },
  created: function () {
    this.currentHost = window.location.host
  }
}
</script>

<style scoped>
.dropdown .dropdown-menu .has-link a.is-active,
a.dropdown-item.is-active,
button.dropdown-item.is-active {
  background-color: #4680df;
  color: #fff;
}
</style>
